import useI18n from '@/shared/hooks/usei18n'
import { Button, GenericDialog } from '@/common'
import CbamSimulationGoodForm, {
  initialState
} from '../components/CbamSimulationGoodForm/CbamSimulationGoodForm'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CbamSimulationGoodFormData,
  validateCbamSimulationGoodForm
} from '../components/CbamSimulationGoodForm/CbamSimulationGoodForm.schema'
import { useGetAllSupplierEmissionsReportsForOrganization } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { useSession } from '@/shared/hooks/useSession'
import {
  CbamSimulationGood,
  useUpdateCbamSimulationGood
} from '@/features/cbam-simulations-v2/hooks/cbamSimulationGoods'
import toast from 'react-hot-toast'
import CbamSimulationGoodDataSection from '../components/CbamSimulationGoodForm/CbamSimulationGoodDataSection'
import { useGetAllInstallationsForOrganization } from '@/features/suppliers/hooks/installations'
import { useGetDefaultValues } from '@/shared/hooks/goods'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'

export default function SimulationGoodEditDialog({
  isOpen,
  close,
  cbamSimulationId,
  simulationGood,
  simulationYear
}: {
  isOpen: boolean
  close: () => void
  cbamSimulationId: string
  simulationGood: CbamSimulationGood
  simulationYear: number
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const supplierEmissionsReportsQuery =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)
  const { mutateAsync: updateSimulationGood, isPending: isUpdatingSimulationGood } =
    useUpdateCbamSimulationGood(organizationId, cbamSimulationId, simulationGood.id)

  const installationsQuery = useGetAllInstallationsForOrganization(organizationId)

  const installations = installationsQuery.data ?? []

  const supplierEmissionsReports = useMemo(
    () => supplierEmissionsReportsQuery.data ?? [],
    [supplierEmissionsReportsQuery.data]
  )

  const { data: goodsCodes } = useGoodsCodes({ aggregatedGoodsCategories: undefined })

  const aggregatedGoodsCategory =
    goodsCodes?.find(goodsCode => goodsCode.goodsCode === simulationGood.cnCode)
      ?.aggregatedGoodsCategory || undefined

  const [formData, setFormData] = useState<CbamSimulationGoodFormData>(initialState)

  const resetFormData = useCallback(() => {
    setFormData({
      aggregatedGoodsCategory,
      cnCode: simulationGood.cnCode,
      country: simulationGood.country,
      description: simulationGood.description ?? '',
      netMassInTonnes: simulationGood.netMassInTonnes,
      isUsingDefaultEmissions: simulationGood.isUsingDefaultEmissions,
      supplierEmissionsReportId: simulationGood.supplierEmissionsReportId,
      importDate: simulationGood.importDate ?? '',
      quarter: simulationGood.quarter ?? null
    })
  }, [simulationGood, aggregatedGoodsCategory])

  useEffect(() => {
    resetFormData()
  }, [resetFormData, simulationGood])

  const validationResult = useCallback(() => {
    return validateCbamSimulationGoodForm(formData)
  }, [formData])

  const selectedSupplierEmissionsReport = useMemo(() => {
    return supplierEmissionsReports.find(report => report.id === formData.supplierEmissionsReportId)
  }, [supplierEmissionsReports, formData.supplierEmissionsReportId])

  const isSaveDisabled = useMemo(() => {
    const validation = validationResult()
    return isUpdatingSimulationGood || !validation.success
  }, [isUpdatingSimulationGood, validationResult])

  const onClose = useCallback(() => {
    resetFormData()
    close()
  }, [close, resetFormData])

  const defaultValueQuery = useGetDefaultValues(formData.cnCode, formData.country, simulationYear)

  const defaultSpecificDirectEmbeddedEmissions = useMemo(() => {
    return defaultValueQuery.data?.directEmissions
      ? Number(defaultValueQuery.data.directEmissions)
      : 0
  }, [defaultValueQuery.data])

  const defaultSpecificIndirectEmbeddedEmissions = useMemo(() => {
    return defaultValueQuery.data?.indirectEmissions
      ? Number(defaultValueQuery.data.indirectEmissions)
      : 0
  }, [defaultValueQuery.data])

  const onSave = useCallback(async () => {
    const validation = validationResult()
    if (!validation.success) {
      return
    }
    trackCbamSimulationSignal('simulations:good:edit:submitted')
    try {
      await updateSimulationGood({
        cnCode: formData.cnCode,
        country: formData.country,
        description: formData.description ?? undefined,
        netMassInTonnes: formData.netMassInTonnes,
        isUsingDefaultEmissions: formData.isUsingDefaultEmissions,
        importDate: formData.importDate,
        supplierEmissionsReportId: formData.supplierEmissionsReportId ?? undefined,
        defaultSpecificDirectEmbeddedEmissions,
        defaultSpecificIndirectEmbeddedEmissions
      })
      trackCbamSimulationSignal('simulations:good:edit:succeeded')
      toast.success(
        t('cbamSimulations:cbamSimulationDetails.dialogs.editSimulationGood.notifications.success')
      )
      onClose()
    } catch (error) {
      console.error(error)
      trackCbamSimulationSignal('simulations:good:edit:failed')
      toast.error(
        t('cbamSimulations:cbamSimulationDetails.dialogs.editSimulationGood.notifications.error')
      )
    }
  }, [
    validationResult,
    updateSimulationGood,
    t,
    formData,
    onClose,
    defaultSpecificDirectEmbeddedEmissions,
    defaultSpecificIndirectEmbeddedEmissions
  ])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('cbamSimulations:cbamSimulationDetails.dialogs.editSimulationGood.title')}
      size="2xl"
    >
      <div className="space-y-4">
        <CbamSimulationGoodForm
          data={formData}
          onChange={setFormData}
          supplierEmissionsReports={supplierEmissionsReports}
          installations={installations}
          simulationYear={simulationYear}
        />
        <CbamSimulationGoodDataSection
          installationCountry={formData.country}
          simulationYear={simulationYear}
          supplierEmissionsReportId={formData.supplierEmissionsReportId}
          selectedSupplierEmissionsReport={selectedSupplierEmissionsReport}
          defaultSpecificDirectEmbeddedEmissions={defaultSpecificDirectEmbeddedEmissions}
          defaultSpecificIndirectEmbeddedEmissions={defaultSpecificIndirectEmbeddedEmissions}
        />
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            disabled={isSaveDisabled}
            isLoading={isUpdatingSimulationGood}
            variant="default"
            onClick={onSave}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </div>
    </GenericDialog>
  )
}
